/**
 * Marker for user-initiated cancellations (e.g. clicking Cancel on the OTP
 * modal mid-payment-verification). When passed to `logError`, routed as
 * `console.warn` rather than `console.error` and skipped from
 * `Sentry.captureException` — these are expected paths in the user funnel,
 * not bugs.
 *
 * Mixpanel session replay, OTel logs, and Sentry breadcrumbs still capture
 * the console.warn line, so cancellation rate stays visible for analytics
 * and debugging.
 *
 * See Sentry CUSTOMER-UI-3M for the original noise this addresses.
 */
export class CancelledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CancelledError";
  }
}

/**
 * Marker for non-Completed OTP verification states (FAILED / EXPIRED /
 * EXHAUSTED). These are expected outcomes in the OTP funnel — the user typed
 * the wrong code, let it expire, or used up their attempts — not application
 * bugs. The user already sees a toast, and OtpProvider already drives the UI
 * transition for each state.
 *
 * When passed to `logError`, routed as `console.warn` rather than
 * `console.error` and skipped from `Sentry.captureException`, so a fumbled
 * code never opens a Sentry error issue. Mixpanel session replay, OTel logs,
 * and Sentry breadcrumbs still capture the warn line, so the failed-OTP rate
 * stays visible for analytics.
 *
 * The `message` is the bare OtopRequestState enum value (e.g. "FAILED") so
 * `isOtopStateError` and the OtpProvider catch block — both of which read
 * `error.message` — keep working unchanged.
 *
 * See Sentry CUSTOMER-UI-3P for the noise this addresses; mirrors the
 * CancelledError pattern (CUSTOMER-UI-3M).
 */
export class OtpStateError extends Error {
  constructor(state: string) {
    super(state);
    this.name = "OtpStateError";
  }
}
